import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/themeColors';

const ExitIntentCTA: React.FC = () => {
  const [showCTA, setShowCTA] = useState(false);
  const { isDark, isHighContrast } = useTheme();
  const colors = getThemeColors('dark', isDark, isHighContrast);

  useEffect(() => {
    let mouseLeaveCount = 0;
    const maxAttempts = 2;
    let hasConfirmedExit = localStorage.getItem('exitConfirmed') === 'true';

    const handleMouseLeave = (e: MouseEvent) => {
      // Verifica se o mouse saiu pela parte superior da tela (indicando que o usuário está tentando fechar)
      if (e.clientY <= 0 && mouseLeaveCount < maxAttempts && !hasConfirmedExit) {
        mouseLeaveCount++;
        setShowCTA(true);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Se o usuário não confirmou a saída, mostra o CTA e impede a saída
      if (!hasConfirmedExit) {
        e.preventDefault();
        e.returnValue = 'Tem certeza que deseja sair? Você pode perder uma economia de até 10% no seu seguro!';
        setShowCTA(true);
        return e.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      // Detecta quando o usuário muda de aba
      if (document.hidden && !hasConfirmedExit) {
        setShowCTA(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Quero fazer uma cotação de seguro automóvel. Vi que você estava prestes a sair, mas não perca a oportunidade de economizar!');
    window.open(`https://wa.me/554791246688?text=${message}`, '_blank');
    // Marca que o usuário confirmou a ação
    localStorage.setItem('exitConfirmed', 'true');
    setShowCTA(false);
  };

  const handleClose = () => {
    // Marca que o usuário confirmou a ação
    localStorage.setItem('exitConfirmed', 'true');
    setShowCTA(false);
  };

  const handleConfirmExit = () => {
    // Marca que o usuário confirmou que quer sair
    localStorage.setItem('exitConfirmed', 'true');
    setShowCTA(false);
    // Permite que o usuário saia
    window.close();
  };

  if (!showCTA) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 'clamp(20px, 5vw, 40px)',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${colors.primaryBg} 0%, ${colors.cardBg} 100%)`,
        borderRadius: 20,
        padding: 'clamp(2rem, 5vw, 3rem)',
        maxWidth: 'clamp(300px, 90vw, 500px)',
        width: '100%',
        textAlign: 'center',
        boxShadow: `0 20px 60px ${colors.shadowColor}`,
        border: `2px solid ${colors.accentText}`,
        position: 'relative',
        animation: 'slideIn 0.5s ease-out',
        transform: 'translateY(0) scale(1)',
        opacity: 1
      }}>
        {/* Botão de fechar */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: 'clamp(10px, 2vw, 15px)',
            right: 'clamp(10px, 2vw, 15px)',
            background: 'none',
            border: 'none',
            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
            color: colors.secondaryText,
            cursor: 'pointer',
            padding: '0.5rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          ✕
        </button>

        {/* Ícone de alerta */}
        <div style={{
          width: 'clamp(60px, 12vw, 80px)',
          height: 'clamp(60px, 12vw, 80px)',
          background: colors.ctaGradient,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto clamp(1rem, 3vw, 1.5rem)',
          color: 'white',
          fontSize: 'clamp(1.5rem, 4vw, 2rem)'
        }}>
          ⚡
        </div>

        <h2 style={{
          fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
          fontWeight: 800,
          marginBottom: 'clamp(0.8rem, 2vw, 1rem)',
          color: colors.primaryText,
          lineHeight: 1.2
        }}>
          🚨 ESPERE!
        </h2>

        <h3 style={{
          fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
          fontWeight: 700,
          marginBottom: 'clamp(1rem, 3vw, 1.5rem)',
          color: colors.accentText,
          lineHeight: 1.3
        }}>
          Você vai perder uma economia de até 10% no seu seguro!
        </h3>

        <p style={{
          fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
          color: colors.secondaryText,
          marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
          lineHeight: 1.5
        }}>
          Milhares de clientes já economizaram com a 7Ases. 
          <br />
          <strong>Faça sua cotação gratuita agora e descubra quanto você pode economizar!</strong>
        </p>

        <div style={{
          display: 'flex',
          gap: 'clamp(0.8rem, 2vw, 1rem)',
          flexWrap: 'wrap',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <button
            onClick={handleWhatsAppClick}
            style={{
              background: `linear-gradient(135deg, #25D366 0%, #128C7E 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: 12,
              padding: 'clamp(0.8rem, 2.5vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
              justifyContent: 'center',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 211, 102, 0.3)';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>💬</span>
            Fazer Cotação Agora
          </button>
          
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              color: colors.primaryText,
              border: `2px solid ${colors.borderColor}`,
              borderRadius: 12,
              padding: 'clamp(0.8rem, 2.5vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.borderColor;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Continuar Navegando
          </button>
          
          <button
            onClick={handleConfirmExit}
            style={{
              background: 'transparent',
              color: '#ef4444',
              border: `2px solid #ef4444`,
              borderRadius: 12,
              padding: 'clamp(0.8rem, 2.5vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ef4444';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#ef4444';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            OK, quero sair
          </button>
        </div>

        <p style={{
          fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
          color: colors.secondaryText,
          marginTop: 'clamp(1rem, 3vw, 1.5rem)',
          opacity: 0.7
                 }}>
           ⏰ Oferta por tempo limitado - Não perca esta oportunidade!
         </p>
       </div>
     </div>
   );
 };

export default ExitIntentCTA; 